import React, { useEffect, useState } from "react";
import { api, fmtEuro, fmtNum } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Loader2, Users, Mail, Phone, MapPin, FileText, Car } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { nom: "", prenom: "", societe: "", telephone: "", email: "", adresse: "", notes: "" };

export default function Clients() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(EMPTY); setEditingId(null); setOpen(true); };
  const openEdit = (c) => {
    setForm({ nom: c.nom, prenom: c.prenom || "", societe: c.societe || "", telephone: c.telephone || "", email: c.email || "", adresse: c.adresse || "", notes: c.notes || "" });
    setEditingId(c.id);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/clients/${editingId}`, form);
        toast.success("Client mis à jour");
      } else {
        await api.post("/clients", form);
        toast.success("Client ajouté");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce client ?")) return;
    await api.delete(`/clients/${id}`);
    toast.success("Client supprimé");
    load();
  };

  const openDetail = async (id) => {
    const { data } = await api.get(`/clients/${id}`);
    setDetail(data);
  };

  const filtered = items.filter((c) => {
    if (!q) return true;
    const s = `${c.nom} ${c.prenom} ${c.email} ${c.telephone}`.toLowerCase();
    return s.includes(q.toLowerCase());
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">Clientèle privée</div>
          <h1 className="font-manrope text-3xl md:text-4xl font-extrabold">Clients</h1>
          <p className="text-sm text-muted-foreground mt-1">{items.length} clients enregistrés</p>
        </div>
        <div className="flex gap-2 w-full md:w-auto">
          <Input placeholder="Rechercher…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" data-testid="input-search-client" />
          <Button onClick={openCreate} data-testid="btn-add-client">
            <Plus className="h-4 w-4 mr-2" /> Nouveau client
          </Button>
        </div>
      </div>

      {/* Cards grid */}
      <div className="card-surface p-2">
        {loading ? (
          <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : filtered.length === 0 ? (
          <div className="border-dashed border-2 border-border/50 m-4 rounded-xl p-10 text-center">
            <Users className="h-10 w-10 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
            <div className="font-manrope font-semibold text-lg mb-1">Aucun client</div>
            <div className="text-sm text-muted-foreground mb-6">Commencez à bâtir votre clientèle privée.</div>
            <Button onClick={openCreate} data-testid="btn-add-client-empty"><Plus className="h-4 w-4 mr-2" /> Nouveau client</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 p-2">
            {filtered.map((c) => (
              <div
                key={c.id}
                className="rounded-lg border border-border p-4 hover:border-primary/40 transition-colors cursor-pointer"
                onClick={() => openDetail(c.id)}
                data-testid={`card-client-${c.id}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-manrope font-semibold text-base">{c.prenom} {c.nom}</div>
                    {c.email && <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1"><Mail className="h-3 w-3" />{c.email}</div>}
                    {c.telephone && <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5"><Phone className="h-3 w-3" />{c.telephone}</div>}
                  </div>
                  <div className="text-right">
                    <div className="overline">Total</div>
                    <div className="num font-manrope font-extrabold text-lg text-primary">{fmtEuro(c.total_spent)}</div>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/60 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Car className="h-3 w-3" /> {fmtNum(c.trips_count)} courses</span>
                  <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => openEdit(c)} className="text-muted-foreground hover:text-primary p-1" data-testid={`btn-edit-client-${c.id}`}>Modifier</button>
                    <button onClick={() => remove(c.id)} className="text-muted-foreground hover:text-destructive p-1" data-testid={`btn-delete-client-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-manrope">{editingId ? "Modifier le client" : "Nouveau client"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-2 gap-3">
            <Field label="Prénom"><Input value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} data-testid="input-client-prenom" /></Field>
            <Field label="Nom"><Input required value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="input-client-nom" /></Field>
            <Field label="Société" full><Input value={form.societe} onChange={(e) => setForm({ ...form, societe: e.target.value })} placeholder="Optionnel" data-testid="input-client-societe" /></Field>
            <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="input-client-email" /></Field>
            <Field label="Téléphone"><Input value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} data-testid="input-client-tel" /></Field>
            <Field label="Adresse" full><Input value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })} data-testid="input-client-adresse" /></Field>
            <Field label="Notes" full><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="input-client-notes" /></Field>
            <DialogFooter className="col-span-2">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={saving} data-testid="btn-save-client">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-manrope">{detail.prenom} {detail.nom}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {detail.email && <InfoRow icon={Mail} value={detail.email} />}
                  {detail.telephone && <InfoRow icon={Phone} value={detail.telephone} />}
                  {detail.adresse && <div className="col-span-2"><InfoRow icon={MapPin} value={detail.adresse} /></div>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <MiniStat label="Courses" value={fmtNum(detail.trips_count)} />
                  <MiniStat label="Total dépensé" value={fmtEuro(detail.total_spent)} tone="primary" />
                </div>

                <div>
                  <div className="overline mb-2">Historique courses</div>
                  {detail.trips.length === 0 ? (
                    <div className="text-sm text-muted-foreground">Aucune course pour ce client.</div>
                  ) : (
                    <div className="max-h-40 overflow-y-auto border border-border rounded-md">
                      <table className="w-full text-xs">
                        <tbody>
                          {detail.trips.map((t) => (
                            <tr key={t.id} className="border-b border-border/60 last:border-b-0">
                              <td className="px-3 py-2 num">{t.date}</td>
                              <td className="px-3 py-2 text-muted-foreground">{t.pickup || "—"} → {t.dropoff || "—"}</td>
                              <td className="px-3 py-2 text-right num font-semibold">{fmtEuro(t.net_amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div>
                  <div className="overline mb-2">Documents</div>
                  {detail.documents.length === 0 ? (
                    <div className="text-sm text-muted-foreground">Aucun devis ou facture.</div>
                  ) : (
                    <div className="space-y-1">
                      {detail.documents.map((d) => (
                        <div key={d.id} className="flex items-center justify-between text-xs px-3 py-2 rounded-md bg-muted/40">
                          <span className="flex items-center gap-2"><FileText className="h-3 w-3 text-primary" /> {d.numero} • {d.type} • {d.date}</span>
                          <span className="num font-semibold">{fmtEuro(d.total)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={`space-y-1.5 ${full ? "col-span-2" : ""}`}>
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}
function InfoRow({ icon: Icon, value }) {
  return <div className="flex items-center gap-2 text-muted-foreground"><Icon className="h-3.5 w-3.5" /><span className="text-foreground text-sm">{value}</span></div>;
}
function MiniStat({ label, value, tone }) {
  return (
    <div className={`rounded-lg border p-3 ${tone === "primary" ? "bg-primary/5 border-primary/25" : "bg-muted/30 border-border"}`}>
      <div className="overline mb-1">{label}</div>
      <div className="font-manrope font-extrabold text-lg num">{value}</div>
    </div>
  );
}
